import {
    Abc as AbcIcon,
    KeyboardReturn as KeyboardReturnIcon,
    PanToolAltOutlined as PanToolAltOutlinedIcon,
} from '@mui/icons-material';
import Box from '@mui/material/Box';
import { grey } from '@mui/material/colors';
import Divider from '@mui/material/Divider';
import ListSubheader from '@mui/material/ListSubheader';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import is from '@sindresorhus/is';
import { Fragment, useContext, useEffect, useRef, useState } from 'react';
import Highlighter from 'react-highlight-words';

import { DropdownSection } from './DropDownSection';
import { ApplicationContext } from '../../../../shared/source/context/context';
import { globalSearchTermSchema } from '../../data-contract/global-search';
import { InlineLoadingSpinner } from '../../design/components/LoadingSpinner';
import { useNavigationToEntity } from '../common/use-navigation-to-entity';

import type { TabParam } from './use-global-search-table-params';
import type { GlobalSearch } from '../../data-contract/global-search';
import type { FunctionComponent, RefObject } from 'react';

const JOBS_TAB = 'jobs';
const SKILLS_TAB = 'skills';
const EMPLOYEES_TAB = 'employees';
const LEARNING_OPPORTUNITIES_TAB = 'learningOpportunities';

type DropdownItemProps = {
    readonly isSelected: boolean;
    readonly onClick: () => void;
    readonly searchTermToHighlight: string;
    readonly title: string;
};

const DropdownItem: FunctionComponent<DropdownItemProps> = ({ isSelected, onClick, searchTermToHighlight, title }) => (
    <MenuItem key={title} selected={isSelected} onClick={onClick}>
        <Highlighter
            autoEscape={true}
            highlightTag="strong"
            searchWords={[searchTermToHighlight]}
            style={{ overflowX: 'hidden', textOverflow: 'ellipsis' }}
            textToHighlight={title}
        />
    </MenuItem>
);

export type DropdownData = {
    readonly id: string;
    readonly type: 'job' | 'learning-opportunity' | 'skill' | 'user';
};

type DropdownContentProps = {
    readonly activeId: string;
    readonly addSkillToSearch: (skillId: string) => void;
    readonly handleGlobalSearch: (searchTerm: string) => void;
    readonly onCloseDropdown: () => void;
    readonly result: GlobalSearch | undefined;
    readonly setSearchTerm: (searchTerm: string) => void;
};

const DropdownFooter: FunctionComponent = () => {
    const { translate } = useContext(ApplicationContext);

    return (
        <ListSubheader sx={{ bgcolor: grey[200], display: 'flex', justifyContent: 'space-between' }}>
            <Box alignItems="center" display="flex">
                <AbcIcon sx={{ color: 'text.primary', mr: 0.5 }} />
                {translate('globalsearch.placeholder-min-length', { count: globalSearchTermSchema.minLength })}
            </Box>
            <Box alignItems="center" display="flex">
                <PanToolAltOutlinedIcon sx={{ color: 'text.primary', mr: 0.5 }} />
                {translate('globalsearch.placeholder-multiple-skills')}
            </Box>
            <Box alignItems="center" display="flex">
                <KeyboardReturnIcon sx={{ color: 'text.primary', mr: 0.5 }} />
                {translate('globalsearch.placeholder-to-search')}
            </Box>
        </ListSubheader>
    );
};

const DropdownContent: FunctionComponent<DropdownContentProps> = ({
    activeId,
    addSkillToSearch,
    handleGlobalSearch,
    onCloseDropdown,
    result,
    setSearchTerm,
    // eslint-disable-next-line complexity
}) => {
    const { navigateToJobPage, navigateToLearningOpportunityPage, navigateToUserProfile } = useNavigationToEntity();
    const { translate } = useContext(ApplicationContext);

    if (is.undefined(result)) {
        return null;
    }

    const clearSearch = (): void => {
        setSearchTerm('');
        onCloseDropdown();
    };

    const redirect = (sectionValue: TabParam): void => {
        handleGlobalSearch(sectionValue);
    };

    const { jobs, learningOpportunities, searchTerm, skills, users } = result;

    const searchTermToHighlight = is.nonEmptyString(searchTerm) ? searchTerm : '';
    const hasSkillsSection = is.nonEmptyArray(skills.data) || skills.failed;
    const hasJobsSection = is.nonEmptyArray(jobs.data);
    const hasUsersSection = is.nonEmptyArray(users.data);
    const hasLearningOpportunitiesSection = is.nonEmptyArray(learningOpportunities.data);

    return (
        <Fragment>
            {hasSkillsSection && (
                <DropdownSection
                    hasMore={skills.hasMore}
                    label={skills.failed ? translate('globalsearch.skills-failed') : translate('globalsearch.skills')}
                    onClick={() => redirect(SKILLS_TAB)}
                >
                    {skills.data.map((skill) => (
                        <DropdownItem
                            key={`search-${skill.id}`}
                            isSelected={skill.id === activeId}
                            searchTermToHighlight={searchTermToHighlight}
                            title={skill.title}
                            onClick={() => {
                                addSkillToSearch(skill.id);
                                clearSearch();
                            }}
                        />
                    ))}
                </DropdownSection>
            )}
            {hasSkillsSection && hasJobsSection && <Divider />}
            {hasJobsSection && (
                <DropdownSection
                    hasMore={jobs.hasMore}
                    label={translate('globalsearch.jobs')}
                    onClick={() => redirect(JOBS_TAB)}
                >
                    {jobs.data.map((job) => (
                        <DropdownItem
                            key={`search-${job.id}`}
                            isSelected={job.id === activeId}
                            searchTermToHighlight={searchTermToHighlight}
                            title={job.title}
                            onClick={() => {
                                navigateToJobPage(job.id);
                                clearSearch();
                            }}
                        />
                    ))}
                </DropdownSection>
            )}
            {(hasSkillsSection || hasJobsSection) && hasUsersSection && <Divider />}
            {hasUsersSection && (
                <DropdownSection
                    hasMore={users.hasMore}
                    label={translate('globalsearch.users')}
                    onClick={() => redirect(EMPLOYEES_TAB)}
                >
                    {users.data.map((user) => (
                        <DropdownItem
                            key={`search-${user.id}`}
                            isSelected={user.id === activeId}
                            searchTermToHighlight={searchTermToHighlight}
                            title={`${user.firstName} ${user.lastName}`}
                            onClick={() => {
                                navigateToUserProfile(user.id);
                                clearSearch();
                            }}
                        />
                    ))}
                </DropdownSection>
            )}
            {(hasSkillsSection || hasJobsSection || hasUsersSection) && hasLearningOpportunitiesSection && <Divider />}
            {hasLearningOpportunitiesSection && (
                <DropdownSection
                    hasMore={learningOpportunities.hasMore}
                    label={translate('globalsearch.learning-opportunities')}
                    onClick={() => redirect(LEARNING_OPPORTUNITIES_TAB)}
                >
                    {learningOpportunities.data.map((learningOpportunity) => (
                        <DropdownItem
                            key={`search-${learningOpportunity.id}`}
                            isSelected={learningOpportunity.id === activeId}
                            searchTermToHighlight={searchTermToHighlight}
                            title={learningOpportunity.title}
                            onClick={() => {
                                navigateToLearningOpportunityPage(learningOpportunity.id);
                                clearSearch();
                            }}
                        />
                    ))}
                </DropdownSection>
            )}
        </Fragment>
    );
};

type DropdownProps = {
    readonly activeId: string;
    readonly addSkillToSearch: (skillId: string) => void;
    readonly handleGlobalSearch: (sectionValue: TabParam) => void;
    readonly isFetching: boolean;
    readonly onCloseDropdown: () => void;
    readonly open: boolean;
    readonly positionRef: RefObject<HTMLElement | null>;
    readonly result: GlobalSearch | undefined;
    readonly searchTerm: string;
    readonly setSearchTerm: (searchTerm: string) => void;
};

export const Dropdown: FunctionComponent<DropdownProps> = ({
    activeId,
    addSkillToSearch,
    handleGlobalSearch,
    isFetching,
    onCloseDropdown,
    open,
    positionRef,
    result,
    searchTerm,
    setSearchTerm,
}) => {
    const width = positionRef.current?.getBoundingClientRect().width ?? 0;

    const [oldSearchTerm, setOldSearchTerm] = useState<string>('');
    const previousResult = useRef<string>('');
    const [displayedResult, setDisplayedResult] = useState(result);
    const [showSpinner, setShowSpinner] = useState<boolean>(true);

    useEffect(() => {
        const isContinuedSearchTerm = searchTerm.startsWith(oldSearchTerm) || oldSearchTerm.startsWith(searchTerm);
        if (!isContinuedSearchTerm || searchTerm.trim() === '') {
            setShowSpinner(true);
            setDisplayedResult(undefined);
        }

        if (searchTerm !== previousResult.current) {
            setOldSearchTerm(previousResult.current);
            previousResult.current = searchTerm;
        }
    }, [searchTerm]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (isFetching === false && is.undefined(result) === false && result !== displayedResult) {
            setShowSpinner(false);
            setDisplayedResult(result);
        }
    }, [isFetching, result]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <Popper
            anchorEl={positionRef.current}
            id="global-search-dropdown"
            open={open}
            placement="bottom"
            sx={{
                borderRadius: '0 0 8px 8px',
                boxShadow: 1,
                overflow: 'hidden',
                width,
                zIndex: 2000,
            }}
        >
            <Paper>
                <MenuList sx={{ maxHeight: '600px', maxWidth: '100%', overflowY: 'scroll', pb: 0, pt: 0 }}>
                    {isFetching && showSpinner ? (
                        <InlineLoadingSpinner />
                    ) : (
                        <DropdownContent
                            activeId={activeId}
                            addSkillToSearch={addSkillToSearch}
                            handleGlobalSearch={handleGlobalSearch}
                            result={displayedResult}
                            setSearchTerm={setSearchTerm}
                            onCloseDropdown={onCloseDropdown}
                        />
                    )}
                </MenuList>
                <DropdownFooter />
            </Paper>
        </Popper>
    );
};
